/**
 * Documents légaux (CGV/CGU, DPA) — règles PURES, partagées serveur + écran.
 *
 * Plan `docs/plans/feat-acceptation-cgv-dpa.md` (décisions de Valérie du 07/10/2026) :
 * - l'ADM dépose une version (PDF français, qui fait foi) avec son jour d'entrée en vigueur ;
 * - la version EN VIGUEUR d'un document est la plus récente, non retirée, dont le jour est arrivé
 *   (jour civil de Paris) ;
 * - une version ne se modifie jamais ; tant qu'elle n'est pas en vigueur — donc jamais acceptée,
 *   puisque seule la version en vigueur peut l'être — elle peut être retirée, puis rétablie.
 *
 * Le jour d'entrée en vigueur d'une nouvelle version ne peut pas être passé (sauf pour la première
 * version d'un document) ni antérieur à celui d'une version existante : l'historique ne se réécrit
 * pas, la version en vigueur à une date donnée reste celle qu'on a affichée ce jour-là.
 */
import { isRealIsoDay } from "./plan-core";

export const LEGAL_DOCUMENT_KINDS = ["cgv", "dpa", "rh_annex", "privacy"] as const;
export type LegalDocumentKind = (typeof LEGAL_DOCUMENT_KINDS)[number];

/**
 * Documents ACCEPTÉS (case à cocher, registre). La politique de confidentialité n'en fait pas
 * partie : elle est mise à disposition pour information (décision de Valérie, 13/10/2026).
 * L'annexe Module RH le sera à la souscription du module RH (pas encore de parcours).
 */
export type AcceptableLegalKind = "cgv" | "dpa" | "rh_annex";

/** Documents sans lesquels la création de compte est fermée. */
export const SIGNUP_REQUIRED_KINDS = ["cgv", "dpa"] as const;

/** Poids maximal d'un PDF déposé (15 Mo). */
export const LEGAL_PDF_MAX_BYTES = 15 * 1024 * 1024;
/** Longueur maximale d'un numéro de version (aligné sur le CHECK de la migration 20261007-1603). */
export const LEGAL_VERSION_MAX_LENGTH = 40;

export function isLegalDocumentKind(v: unknown): v is LegalDocumentKind {
  return typeof v === "string" && (LEGAL_DOCUMENT_KINDS as readonly string[]).includes(v);
}

/** Ce que les règles lisent d'une version (jamais le PDF). */
export interface LegalVersionLite {
  id: string;
  kind: LegalDocumentKind;
  version: string;
  /** Jour d'entrée en vigueur, `AAAA-MM-JJ` (jour civil de Paris). */
  effectiveOn: string;
  withdrawnAt: string | Date | null;
}

/** Jour civil de Paris (`AAAA-MM-JJ`) à l'instant donné : le serveur tourne en heure universelle. */
export function parisDay(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** Version en vigueur à ce jour : non retirée et jour d'entrée en vigueur arrivé. */
export function isInForce(v: LegalVersionLite, today: string): boolean {
  return !v.withdrawnAt && v.effectiveOn <= today;
}

/** Version en vigueur d'un document à ce jour, ou `null` s'il n'en a aucune. */
export function currentLegalVersion<T extends LegalVersionLite>(versions: T[], kind: LegalDocumentKind, today: string): T | null {
  let best: T | null = null;
  for (const v of versions) {
    if (v.kind !== kind || !isInForce(v, today)) continue;
    if (!best || v.effectiveOn > best.effectiveOn) best = v;
  }
  return best;
}

export type LegalVersionStatus = "current" | "scheduled" | "superseded" | "withdrawn";

/** État affiché d'une version : en vigueur, programmée, remplacée ou retirée. */
export function legalVersionStatus(v: LegalVersionLite, versions: LegalVersionLite[], today: string): LegalVersionStatus {
  if (v.withdrawnAt) return "withdrawn";
  if (v.effectiveOn > today) return "scheduled";
  return currentLegalVersion(versions, v.kind, today)?.id === v.id ? "current" : "superseded";
}

export type LegalVersionRefusal =
  | "legal_kind_invalid"
  | "legal_version_required"
  | "legal_effective_day_invalid"
  | "legal_version_exists"
  | "legal_effective_day_taken"
  | "legal_effective_day_past"
  | "legal_effective_day_before_existing";

/**
 * Refus éventuel d'une nouvelle version (ou du rétablissement d'une version retirée : passer les
 * AUTRES versions dans `existing`). `null` = acceptée.
 */
export function newLegalVersionRefusal(
  existing: LegalVersionLite[],
  input: { kind: unknown; version: unknown; effectiveOn: unknown },
  today: string,
): LegalVersionRefusal | null {
  if (!isLegalDocumentKind(input.kind)) return "legal_kind_invalid";
  const version = typeof input.version === "string" ? input.version.trim() : "";
  if (!version || version.length > LEGAL_VERSION_MAX_LENGTH) return "legal_version_required";
  if (!isRealIsoDay(input.effectiveOn)) return "legal_effective_day_invalid";
  const same = existing.filter((v) => v.kind === input.kind && !v.withdrawnAt);
  if (same.some((v) => v.version.trim().toLowerCase() === version.toLowerCase())) return "legal_version_exists";
  if (same.some((v) => v.effectiveOn === input.effectiveOn)) return "legal_effective_day_taken";
  if (same.length === 0) return null; // première version : un jour passé est permis (mise en service)
  if (input.effectiveOn < today) return "legal_effective_day_past";
  if (same.some((v) => v.effectiveOn > (input.effectiveOn as string))) return "legal_effective_day_before_existing";
  return null;
}

export type LegalWithdrawRefusal = "legal_already_withdrawn" | "legal_version_locked";

/** Une version ne se retire que si elle n'est pas encore en vigueur (jamais acceptée). */
export function withdrawLegalVersionRefusal(v: LegalVersionLite, today: string): LegalWithdrawRefusal | null {
  if (v.withdrawnAt) return "legal_already_withdrawn";
  if (v.effectiveOn <= today) return "legal_version_locked";
  return null;
}

/** Un fichier est-il un PDF (signature `%PDF-` en tête) ? */
export function looksLikePdf(bytes: Uint8Array): boolean {
  return bytes.length > 5 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d;
}
